import React from "react";
import { isInternalLink } from "./linkUtils";

const IMAGE_DOMAIN = "https://media.dinamani.com/";

// Image metadata type for focus-point support
export type ImageMetadata = {
  'focus-point'?: [number, number];
  width?: number;
  height?: number;
};

// Check if focus-point exists in image metadata
export function hasFocusPoint(metadata?: ImageMetadata): boolean {
  return !!(metadata?.['focus-point'] && metadata.width && metadata.height);
}

// Calculate focus point as percentage for object-position
// If focus-point exists, returns { objectPosition: "X% Y%" }
// If not, returns { objectPosition: 'top' } as default
export function getFocusPointStyle(metadata?: ImageMetadata): React.CSSProperties {
  if (!hasFocusPoint(metadata)) {
    return { objectPosition: 'top' };
  }
  const [focusX, focusY] = metadata!['focus-point']!;
  const xPercent = Math.round((focusX / metadata!.width!) * 100);
  const yPercent = Math.round((focusY / metadata!.height!) * 100);
  return { objectPosition: `${xPercent}% ${yPercent}%` };
}

// Tiny 1x1 gray blur placeholder for images (base64 encoded)
// Use this with Next.js Image component: placeholder="blur" blurDataURL={BLUR_PLACEHOLDER}
export const BLUR_PLACEHOLDER = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAn/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBEQCEAwEPwAB//9k=";

// Image URL function
export function getImageUrl(value?: string): string {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return IMAGE_DOMAIN + value;
}


// Tamil month names
const TAMIL_MONTHS = [
  "ஜனவரி",
  "பிப்ரவரி",
  "மார்ச்",
  "ஏப்ரல்",
  "மே",
  "ஜூன்",
  "ஜூலை",
  "ஆகஸ்ட்",
  "செப்டம்பர்",
  "அக்டோபர்",
  "நவம்பர்",
  "டிசம்பர்",
];

// Time ago function (Tamil format)
export function getTimeAgo(timestamp?: string | number): string {
  if (!timestamp) return "";

  const now = Date.now();
  const numericTimestamp = typeof timestamp === "string" ? Number(timestamp) : timestamp;

  // If the value is already a formatted string (not a valid number), return it as-is
  if (isNaN(numericTimestamp)) return String(timestamp);

  // Detect if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Timestamps less than 10^12 (year 2001 in ms) are likely in seconds
  const time = numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp;

  const diffMs = now - time;
  if (diffMs < 0) return "";

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Less than 1 minute - "இப்போது"
  if (diffMinutes < 1) return "இப்போது";

  // 1 minute to 59 minutes - "X நிமிடம் முன்பு" / "X நிமிடங்கள் முன்பு"
  if (diffMinutes < 60) {
    return diffMinutes === 1
      ? "1 நிமிடம் முன்பு"
      : `${diffMinutes} நிமிடங்கள் முன்பு`;
  }

  // 1 hour to 24 hours - "X மணி நேரம் முன்பு" / "X மணி நேரங்கள் முன்பு"
  if (diffHours < 24) {
    return diffHours === 1
      ? "1 மணி நேரம் முன்பு"
      : `${diffHours} மணி நேரங்கள் முன்பு`;
  }

  // 24 hours or more - show exact date
  const date = new Date(time);
  const day = date.getDate();
  const month = TAMIL_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}


// Build sticky CSS styles from column config
// When sticky: true, applies position sticky with configurable top offset
// Columns with tabBar return {} here — tab header sticky is controlled by
// stickyTabs: true in JSON config, passed to TabBarSection separately
export function getStickyStyles(config: {
  sticky?: boolean;
  stickyTop?: string;
  hasTabBar?: boolean;
}): React.CSSProperties {
  if (!config.sticky) return {};

  // TabBar columns: no sticky on the wrapper — it moves to TabBarSection's tab header
  if (config.hasTabBar) return {};

  const top = config.stickyTop || '150px';

  // Regular sticky columns: constrain height and allow inner scroll
  return {
    position: 'sticky',
    top,
    alignSelf: 'flex-start',
    zIndex: 40,
    maxHeight: `calc(100vh - ${top} - 20px)`,
    overflowY: 'auto',
  };
}

// Section URL function - extracts path from full URL
// Example: "https://www.dinamani.com/visual-stories" → "/visual-stories"
export function getSectionUrl(url?: string): string {
  if (!url) return "";

  // If already a relative path, return as-is
  if (url.startsWith("/")) return url;

  // If it's a full URL, extract the pathname
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname || "/";
    } catch {
      return "";
    }
  }

  // If it's just a slug without leading slash, add one
  return `/${url}`;
}

/**
 * Get link properties for widget links (image widget, etc.)
 *
 * Determines whether to use Next.js Link (internal navigation) or <a> tag (external).
 * - Internal links (e.g., "/t20worldcup") → useNextLink: true, href for Next.js Link
 * - External links with _blank → useNextLink: false, target="_blank", rel="noopener noreferrer"
 * - External links with _self → useNextLink: false, target="_self"
 *
 * linkTarget from JSON:
 *   "_blank" → open in new tab
 *   "_self"  → open in same tab
 */
export function getWidgetLinkProps(link?: string, linkTarget?: string): {
  useNextLink: boolean;
  href: string;
  target?: string;
  rel?: string;
} | null {
  if (!link) return null;

  const internal = isInternalLink(link);

  // Internal links: always use Next.js Link for client-side navigation
  if (internal) {
    // Ensure internal links start with "/"
    const href = link.startsWith('/') ? link : getSectionUrl(link);
    return {
      useNextLink: true,
      href,
      target: linkTarget === '_blank' ? '_blank' : undefined,
      rel: linkTarget === '_blank' ? 'noopener noreferrer' : undefined,
    };
  }

  // External links
  if (linkTarget === '_blank') {
    return {
      useNextLink: false,
      href: link,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  // External link with _self (open in same tab)
  return {
    useNextLink: false,
    href: link,
    target: '_self',
  };
}
